import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Instagram, Music2, AtSign } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { APP_LOGO, roleHome, SOCIALS } from "@/lib/constants";

const SOCIAL_ICON = { Instagram, TikTok: Music2, Threads: AtSign };

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={roleHome(user.role)} replace />;

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(""); setLoading(true);
    try { const u = await login(email, password); window.location.assign(roleHome(u.role)); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Login gagal"); }
    finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/auth/google";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src="https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Luxury wedding venue" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-burgundy-dark/70 via-burgundy/40 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14 text-white">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold-light mb-3">PRIMROSE ORGANIZER WITH YOU</p>
          <h2 className="font-heading text-5xl leading-tight">Setiap momen berharga,<br />terkelola sempurna.</h2>
          <p className="mt-4 text-sm tracking-[0.2em] text-gold-light font-semibold">#TAKEYOURLOVEWITHPRIMROSE</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-cream">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex justify-center mb-8">
            <img src={APP_LOGO} alt="Primrose Organizer" className="h-40 w-auto object-contain" data-testid="login-logo" />
          </div>

          <h3 className="font-heading text-3xl text-[#1A1A1A] mb-1">Selamat datang</h3>
          <p className="text-sm text-[#7A7A7A] mb-8">Masuk untuk mengelola operasional Anda.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-[#4A4A4A] mb-1.5 block">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#4A4A4A] mb-1.5 block">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-white" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" className="text-xs text-burgundy hover:underline" data-testid="forgot-password-link">Lupa password?</Link>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-burgundy hover:bg-burgundy-dark text-white rounded-full py-6 text-sm font-semibold">
              {loading ? "Memproses..." : "Masuk"}
            </Button>

            <div className="flex items-center gap-3 my-1">
              <span className="flex-1 h-px bg-gold/30" />
              <span className="text-[11px] uppercase tracking-wider text-[#9A9A9A]">atau</span>
              <span className="flex-1 h-px bg-gold/30" />
            </div>
            <button type="button" onClick={googleSignIn} data-testid="google-signin-btn" className="w-full flex items-center justify-center gap-2.5 bg-white border border-gold/30 hover:border-burgundy/40 text-[#3A3A3A] rounded-full py-3 text-sm font-medium transition-colors">
              <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Masuk dengan Google
            </button>

            <Link to="/register" className="block text-center mt-4 text-sm text-[#7A7A7A] hover:text-burgundy" data-testid="register-link">Client baru? <span className="text-burgundy font-semibold">Daftar di sini</span></Link>
          </form>

          <div className="mt-8 pt-6 border-t border-gold/20 text-center">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-3">Ikuti Primrose Organizer</p>
            <div className="flex items-center justify-center gap-3" data-testid="login-socials">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICON[s.name] || AtSign;
                return (
                  <a key={s.name} href={s.url} target="_blank" rel="noreferrer" aria-label={s.name} title={s.name}
                    className="w-10 h-10 rounded-full bg-white border border-gold/30 text-burgundy flex items-center justify-center transition-all hover:bg-burgundy hover:text-white hover:border-burgundy hover:-translate-y-0.5"
                    data-testid={`login-social-${s.name.toLowerCase()}`}>
                    <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
